import { frappeList, frappeGet } from "./utils";
import { getERPClient } from "./client";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { docTypeListWithPagination } from "./docs";

/**
 * Default fields to fetch for Stock Reconciliation list views
 *
 * This constant ensures consistency across:
 * - stockReconciliations.list() (legacy, if needed)
 * - stockReconciliations.listWithPagination() defaultFields
 * - useStockReconciliationListWithPagination fields
 * - createStockReconciliationColumns accessorKeys
 *
 * IMPORTANT:
 * - If you add a new column to createStockReconciliationColumns, add the corresponding field here.
 * - Note: "modified" can be used for sorting even if not included here. If you add a
 *   "Last Updated" column later, add "modified" to this array.
 */
export const STOCK_RECONCILIATION_LIST_FIELDS = [
  "name",
  "posting_date",
  "posting_time",
  "purpose",
  "company",
  "set_warehouse",  // Default Warehouse
  "expense_account",  // Difference Account
  "cost_center",
  "difference_amount",
  "docstatus",  // Real field: 0=Draft, 1=Submitted, 2=Cancelled
] as const;

export interface StockReconciliationListParams {
  /**
   * Pre-mapped Frappe filter array, e.g.:
   * [["Stock Reconciliation", "purpose", "=", "Opening Stock"]]
   */
  filters?: FrappeFilterArray;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export type StockReconciliation = {
  name: string;
  posting_date: string;
  posting_time?: string;
  purpose: "Opening Stock" | "Stock Reconciliation";
  company?: string;
  set_warehouse?: string;  // Default Warehouse
  expense_account?: string;  // Difference Account
  cost_center?: string;
  difference_amount?: number;
  docstatus: 0 | 1 | 2;  // 0=Draft, 1=Submitted, 2=Cancelled
  // Allow additional backend fields without breaking typing
  [key: string]: any;
};

export const stockReconciliations = {
  /**
   * List all stock reconciliations (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<StockReconciliation[]> {
    return frappeList<StockReconciliation>(
      "Stock Reconciliation",
      filters,
      [...STOCK_RECONCILIATION_LIST_FIELDS]
    );
  },

  /**
   * List stock reconciliations with server-side pagination and filtering.
   *
   * This currently uses the generic docTypeListWithPagination path without
   * any special logic. If you later need Stock Reconciliation–specific backend
   * behavior, you can add it here or use docTypeListWithPaginationViaMethod.
   */
  listWithPagination(
    params: StockReconciliationListParams
  ): Promise<FrappeListResponse<StockReconciliation>> {
    const {
      filters = [],
      pageIndex = 0,
      pageSize = 20,
      orderBy,
      fields,
    } = params;

    const defaultFields = fields ?? [...STOCK_RECONCILIATION_LIST_FIELDS];

    return docTypeListWithPagination<StockReconciliation>({
      doctype: "Stock Reconciliation",
      filters,
      pageIndex,
      pageSize,
      orderBy: orderBy ?? "modified desc",
      fields: defaultFields,
    });
  },

  /**
   * Get a single stock reconciliation by name
   */
  detail(name: string): Promise<StockReconciliation> {
    return frappeGet<StockReconciliation>("Stock Reconciliation", name);
  },

  /**
   * Create a new stock reconciliation
   */
  create(data: Record<string, any>): Promise<StockReconciliation> {
    const client = getERPClient();
    return client.create({ doctype: "Stock Reconciliation", data });
  },

  /**
   * Get valuation rate for Stock Reconciliation quick action modal.
   * Mirrors ERPNext Stock Reconciliation valuation defaulting (stock balance valuation).
   */
  async getQuickActionValuationRate(params: {
    item_code: string;
    warehouse: string;
    company?: string;
    posting_date?: string;
    posting_time?: string;
  }): Promise<{ valuation_rate: number }> {
    const client = getERPClient();
    return client.call<{ valuation_rate: number }>(
      "zaviago_backend.api.stock_reconciliation.get_quick_action_valuation_rate",
      params
    );
  },

  /**
   * Update an existing stock reconciliation
   */
  update(name: string, data: Record<string, any>): Promise<StockReconciliation> {
    const client = getERPClient();
    return client.update({ doctype: "Stock Reconciliation", name, data });
  },

  /**
   * Delete a stock reconciliation
   */
  delete(name: string): Promise<void> {
    const client = getERPClient();
    return client.delete({ doctype: "Stock Reconciliation", name });
  },
};

